import React from 'react';
import { Button } from '../../ui/button';
import { Card, CardContent } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Clock, CheckCircle, XCircle, Mail, Phone, MapPin, Award } from 'lucide-react';
import { getTypeLabel } from './professionalConstants';

export default function ApplicationsTab({
  pendingApplications,
  expandedProf,
  setExpandedProf,
  onApprove,
  onReject,
}) {
  if (pendingApplications.length === 0) {
    return (
      <div className="space-y-4">
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-8 text-center">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">All Caught Up!</h3>
            <p className="text-slate-400">No pending professional applications to review.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {pendingApplications.map(app => (
        <Card key={app.profile_id} className="bg-slate-800 border-slate-700 border-l-4 border-l-amber-500">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <h3 className="text-lg font-semibold text-white">{app.name}</h3>
                  <Badge className="bg-amber-600 text-white">
                    <Clock className="h-3 w-3 mr-1" />
                    Pending Review
                  </Badge>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-amber-400 font-medium">{getTypeLabel(app.professional_type)}</p>
                    {app.company && <p className="text-slate-400 text-sm">{app.company}</p>}
                  </div>
                  <div className="space-y-1">
                    {app.email && (
                      <p className="text-slate-400 text-sm flex items-center gap-2">
                        <Mail className="h-3 w-3" /> {app.email}
                      </p>
                    )}
                    {app.phone && (
                      <p className="text-slate-400 text-sm flex items-center gap-2">
                        <Phone className="h-3 w-3" /> {app.phone}
                      </p>
                    )}
                    {app.location && (
                      <p className="text-slate-400 text-sm flex items-center gap-2">
                        <MapPin className="h-3 w-3" /> {app.location}
                      </p>
                    )}
                  </div>
                </div>

                {app.bio && (
                  <div className="mb-3">
                    <p className={`text-slate-300 text-sm ${expandedProf === app.profile_id ? '' : 'line-clamp-2'}`}>{app.bio}</p>
                    {app.bio.length > 120 && (
                      <button
                        onClick={() => setExpandedProf(expandedProf === app.profile_id ? null : app.profile_id)}
                        className="text-amber-400 text-xs mt-1 hover:underline"
                        data-testid={`toggle-bio-${app.profile_id}`}
                      >
                        {expandedProf === app.profile_id ? 'Show less' : 'View full bio'}
                      </button>
                    )}
                  </div>
                )}

                <div className="flex flex-wrap gap-1 mb-4">
                  {(app.specialties || []).slice(0, 4).map((spec, i) => (
                    <Badge key={i} variant="secondary" className="bg-slate-700 text-slate-300 text-xs">
                      {spec}
                    </Badge>
                  ))}
                </div>

                {app.referred_by && (
                  <p className="text-sm text-green-400">
                    <Award className="h-3 w-3 inline mr-1" />
                    Referred by another professional
                  </p>
                )}

                <p className="text-xs text-slate-500 mt-2">
                  Applied: {new Date(app.created_at).toLocaleDateString()}
                </p>
              </div>

              <div className="flex flex-col gap-2 ml-4">
                <Button
                  onClick={() => onApprove(app)}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  data-testid={`approve-application-${app.profile_id}`}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Approve
                </Button>
                <Button
                  onClick={() => onReject(app)}
                  variant="outline"
                  className="border-red-500 text-red-400 hover:bg-red-500/10"
                  data-testid={`reject-application-${app.profile_id}`}
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Reject
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
